import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, MenuItem, Paper, Select,
  Stack, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';

const ROLE_COLORS = { admin: 'error', organiser: 'primary', viewer: 'default' };

const OrgManagement = ({ orgId, orgRole, onSnack }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [events, setEvents] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Edit org details state
  const [editOrgOpen, setEditOrgOpen] = useState(false);
  const [editOrgForm, setEditOrgForm] = useState({});
  const [editOrgLoading, setEditOrgLoading] = useState(false);

  // Approve join request
  const [approvingId, setApprovingId] = useState(null);
  const [approveRole, setApproveRole] = useState('viewer');

  const isAdmin = orgRole === 'admin';

  const loadData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [orgData, membersData, eventsData] = await Promise.all([
        api.getOrg(orgId),
        api.getOrgMembers(orgId),
        api.getOrgEvents(orgId),
      ]);
      setOrg(orgData.org);
      setMembers(membersData.members || []);
      setJoinRequests(membersData.join_requests || []);
      setEvents(eventsData.events || []);
    } catch (err) {
      onSnack?.('Failed to load organisation data', 'error');
    } finally {
      setLoading(false);
    }
  }, [orgId, onSnack]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAudit = useCallback(async () => {
    if (!orgId || !isAdmin) return;
    setAuditLoading(true);
    try {
      const data = await api.getOrgAudit(orgId);
      setAuditLog(data.audit_log || []);
    } catch {
      onSnack?.('Failed to load audit log', 'error');
    } finally {
      setAuditLoading(false);
    }
  }, [orgId, isAdmin, onSnack]);

  useEffect(() => {
    if (tab === 3 && isAdmin) loadAudit();
  }, [tab, loadAudit, isAdmin]);

  const handleUpdateOrgDetails = async () => {
    setEditOrgLoading(true);
    try {
      await api.updateOrg(orgId, editOrgForm);
      onSnack?.('Organisation updated');
      setEditOrgOpen(false);
      loadData();
    } catch (err) {
      onSnack?.(err.message || 'Update failed', 'error');
    } finally {
      setEditOrgLoading(false);
    }
  };

  const handleRemoveMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from this organisation?`)) return;
    try {
      await api.removeMember(orgId, userId);
      onSnack?.('Member removed');
      loadData();
    } catch (err) {
      onSnack?.(err.message || 'Failed to remove member', 'error');
    }
  };

  const handleChangeRole = async (userId, role) => {
    try {
      await api.updateMemberRole(orgId, userId, role);
      onSnack?.('Role updated');
      loadData();
    } catch (err) {
      onSnack?.(err.message || 'Failed to update role', 'error');
    }
  };

  const handleApproveJoin = async (requestId) => {
    try {
      await api.approveJoinRequest(orgId, requestId, approveRole);
      onSnack?.('Request approved');
      setApprovingId(null);
      loadData();
    } catch (err) {
      onSnack?.(err.message || 'Approval failed', 'error');
    }
  };

  const handleRejectJoin = async (requestId) => {
    try {
      await api.rejectJoinRequest(orgId, requestId);
      onSnack?.('Request rejected');
      loadData();
    } catch (err) {
      onSnack?.(err.message || 'Rejection failed', 'error');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  const tabLabels = isAdmin
    ? ['Details', 'Members', 'Events', 'Audit Log']
    : ['Details', 'Members', 'Events'];

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {tabLabels.map(l => <Tab key={l} label={l} />)}
        {isAdmin && joinRequests.length > 0 && tab !== 1 && (
          <Chip
            label={`${joinRequests.length} pending`}
            color="warning"
            size="small"
            sx={{ alignSelf: 'center', ml: 1 }}
          />
        )}
      </Tabs>

      {/* ── Details tab ── */}
      {tab === 0 && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="h6" fontWeight={700}>{org?.org_name}</Typography>
                {org?.org_type && (
                  <Typography variant="body2" color="text.secondary">{org.org_type}</Typography>
                )}
                {org?.website && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <a href={org.website} target="_blank" rel="noopener noreferrer"
                       style={{ color: 'inherit' }}>
                      {org.website}
                    </a>
                  </Typography>
                )}
                {org?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {org.description}
                  </Typography>
                )}
              </Box>
              {isAdmin && (
                <IconButton
                  size="small"
                  onClick={() => { setEditOrgForm({ org_name: org?.org_name || '', org_type: org?.org_type || '', website: org?.website || '', description: org?.description || '' }); setEditOrgOpen(true); }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`Join code: ${org?.join_code}`} variant="outlined" size="small" sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2 }} />
              <Chip label={`Members: ${members.length}`} size="small" variant="outlined" />
              <Chip label={`Events: ${events.length}`} size="small" variant="outlined" />
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* ── Members tab ── */}
      {tab === 1 && (
        <Stack spacing={3}>
          {/* Pending join requests (admin only) */}
          {isAdmin && joinRequests.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Pending Join Requests ({joinRequests.length})
              </Typography>
              <Stack spacing={1}>
                {joinRequests.map(jr => (
                  <Paper key={jr.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{jr.customer_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{jr.email}</Typography>
                        {jr.message && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                            "{jr.message}"
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Requested: {jr.requested_role}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {approvingId === jr.id ? (
                          <>
                            <Select
                              size="small"
                              value={approveRole}
                              onChange={e => setApproveRole(e.target.value)}
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value="admin">Admin</MenuItem>
                              <MenuItem value="organiser">Organiser</MenuItem>
                              <MenuItem value="viewer">Viewer</MenuItem>
                            </Select>
                            <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />}
                              onClick={() => handleApproveJoin(jr.id)}>
                              Confirm
                            </Button>
                            <IconButton size="small" onClick={() => setApprovingId(null)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <Button size="small" variant="outlined" color="success"
                              onClick={() => { setApprovingId(jr.id); setApproveRole(jr.requested_role || 'viewer'); }}>
                              Approve
                            </Button>
                            <Button size="small" variant="outlined" color="error"
                              onClick={() => handleRejectJoin(jr.id)}>
                              Reject
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {/* Member list */}
          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Members</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  {isAdmin && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map(m => (
                  <TableRow key={m.id}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                          {(m.customer_name || '?')[0].toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">{m.customer_name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{m.email}</Typography>
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Select
                          size="small"
                          value={m.role}
                          onChange={e => handleChangeRole(m.user_customer_id, e.target.value)}
                          variant="standard"
                        >
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="organiser">Organiser</MenuItem>
                          <MenuItem value="viewer">Viewer</MenuItem>
                        </Select>
                      ) : (
                        <Chip label={m.role} color={ROLE_COLORS[m.role]} size="small" />
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Tooltip title="Remove member">
                          <IconButton size="small" color="error"
                            onClick={() => handleRemoveMember(m.user_customer_id, m.customer_name)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      )}

      {/* ── Events tab ── */}
      {tab === 2 && (
        <Box>
          {events.length === 0 ? (
            <Alert severity="info">No events under this organisation yet.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Event</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map(ev => (
                  <TableRow key={ev.event_id}>
                    <TableCell><Typography variant="body2">{ev.event_name}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {ev.event_date ? new Date(ev.event_date).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{ev.event_type || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={ev.status} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => navigate(`/event/${ev.event_id}`)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {/* ── Audit log tab (admin only) ── */}
      {tab === 3 && isAdmin && (
        <Box>
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : auditLog.length === 0 ? (
            <Alert severity="info">No audit entries yet.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Who</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLog.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Typography variant="body2">{row.actor_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={row.action} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {row.target_type ? `${row.target_type} ${row.target_id || ''}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(row.created_at).toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {/* Edit org dialog (admin only) */}
      <Dialog open={editOrgOpen} onClose={() => setEditOrgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organisation Details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Organisation Name" fullWidth required
              value={editOrgForm.org_name || ''}
              onChange={e => setEditOrgForm(f => ({ ...f, org_name: e.target.value }))}
            />
            <TextField
              label="Type" fullWidth
              value={editOrgForm.org_type || ''}
              onChange={e => setEditOrgForm(f => ({ ...f, org_type: e.target.value }))}
            />
            <TextField
              label="Website" fullWidth
              value={editOrgForm.website || ''}
              onChange={e => setEditOrgForm(f => ({ ...f, website: e.target.value }))}
            />
            <TextField
              label="Description" fullWidth multiline rows={3}
              value={editOrgForm.description || ''}
              onChange={e => setEditOrgForm(f => ({ ...f, description: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrgOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateOrgDetails}
            disabled={editOrgLoading || !editOrgForm.org_name?.trim()}
            startIcon={editOrgLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrgManagement;
